import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Reveal, SectionLabel, renderPh, scrollToId, Ph, SectionBg } from "./fx";
import { ParticleHero } from "./ParticleHero";
import { RegForm } from "./RegForm";
import { Countdown } from "./Countdown";
import { PH } from "./copy";

const CTA = ({ label, testId }) => (
  <button data-testid={testId} data-cta onClick={() => scrollToId("register")} className="btn-accent px-8 py-4 text-sm tracking-widest uppercase">
    {label}
  </button>
);

export const Hero = ({ t, lang }) => {
  const heroRef = useRef(null);
  return (
    <section ref={heroRef} className="relative min-h-screen grid-bg flex items-center pt-28 pb-16 md:pt-24 overflow-hidden" data-testid="hero-section">
      <SectionBg img="/img/home1.jpg" opacity={0.32} />
      <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
        <div className="relative z-10">
          <div className="mono text-[10px] tracking-[0.25em] text-white/30 mb-4" data-testid="hero-date-line">{renderPh(t.dateLine)}</div>
          <Countdown t={t} />
          <div className="mono accent text-[10px] tracking-[0.25em] mb-6 max-w-md leading-relaxed" data-testid="hero-eyebrow">{renderPh(t.eyebrow)}</div>
          <h1 className="h1-display text-[clamp(34px,4.1vw,64px)]" data-testid="hero-h1">
            {t.h1.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden">
                <motion.span
                  className={`block ${i === 1 ? "accent" : ""}`}
                  style={i === 2 ? { opacity: 0.6, fontSize: "62%" } : {}}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <p className="mt-6 text-[15px] text-white/55 max-w-lg leading-relaxed" data-testid="hero-subheadline">
            {t.sub.map((s, i) => (s.a ? <span key={i} className="accent">{s.t}</span> : <span key={i}>{s.t}</span>))}
          </p>
          <blockquote className="mt-6 border-l-2 border-[#4D9FFF] pl-5 italic text-sm text-white/60 max-w-md leading-relaxed" data-testid="hero-blockquote">
            {t.quote}
          </blockquote>
          <div className="mt-8">
            <CTA label={t.cta} testId="hero-cta-button" />
            <div className="mt-3 text-[11px] text-white/25 mono tracking-wider">{t.ctaSub}</div>
          </div>
          <div className="mt-10 flex items-center gap-4" data-testid="hero-host-module">
            <img src="/img/plinio.webp" alt="Plinio Angulo, Loan Officer, NMLS #1443167" loading="lazy" className="w-12 h-12 rounded-full border-2 border-[#4D9FFF] object-cover object-top shrink-0" />
            <div className="text-xs leading-relaxed">
              <div className="font-bold">{t.hostName}</div>
              <div className="text-white/40">{PH.company}</div>
              <div className="text-white/40 mono text-[10px]">NMLS# {PH.nmls}</div>
            </div>
          </div>
        </div>

        <div className="relative min-h-[560px] flex items-center justify-center">
          <div className="hidden md:block absolute inset-[-15%]">
            <ParticleHero heroRef={heroRef} />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 glass-card p-6 md:p-8 w-full max-w-md"
            data-testid="hero-registration-card"
          >
            <div className="mono accent text-[10px] tracking-[0.35em] mb-2">{t.card.label}</div>
            <h3 className="font-black text-lg uppercase mb-4">{t.card.header}</h3>
            <ul className="space-y-2.5 mb-4">
              {t.card.bonuses.map((b, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] leading-snug">
                  <span className="accent font-black shrink-0">✓</span>
                  <span className="text-white/75">
                    {b.t}{" "}
                    {b.ph && <Ph>{b.ph}</Ph>}
                  </span>
                </li>
              ))}
            </ul>
            <div className="accent font-black text-[22px] mb-4">{t.card.free}</div>
            <RegForm t={t} lang={lang} idPrefix="hero" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const Ticker = ({ t }) => (
  <section className="border-y border-white/10 py-4 overflow-hidden" data-testid="proof-ticker">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center">
          {t.ticker.map((item, i) => (
            <span key={i} className="mono text-[11px] tracking-[0.2em] text-white/40 flex items-center whitespace-nowrap">
              <span className="px-6">{renderPh(item)}</span>
              <span className="accent">◆</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

export const Manifesto = ({ t, lang }) => (
  <section className="relative overflow-hidden py-28 md:py-40 px-6 md:px-12" data-testid="manifesto-section">
    <SectionBg img="/img/home2.jpg" />
    <div className="relative max-w-[1400px] mx-auto grid md:grid-cols-[1fr_auto] gap-16 items-start">
      <div>
        <SectionLabel>{t.manifesto.label}</SectionLabel>
        <h2 className="h1-display text-[clamp(36px,5.4vw,68px)]" style={{ lineHeight: 0.97 }}>
          {t.manifesto.lines.map((line, li) => (
            <span key={`${lang}-${li}`} className={`block ${line.c === "a" ? "accent" : ""}`} style={line.c === "m" ? { opacity: 0.28 } : {}}>
              {line.t.split(" ").map((w, wi) => (
                <motion.span
                  key={wi}
                  className="inline-block mr-[0.25em]"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.55, delay: (li * 3 + wi) * 0.04, ease: [0.22, 1, 0.36, 1] }}
                >
                  {w}
                </motion.span>
              ))}
            </span>
          ))}
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-10 text-base text-white/55 max-w-[560px]" style={{ lineHeight: 1.75 }}>{t.manifesto.body}</p>
          <button data-testid="manifesto-cta" data-cta onClick={() => scrollToId("register")} className="btn-outline mt-8 px-8 py-4 text-sm uppercase">
            {t.manifesto.cta}
          </button>
        </Reveal>
      </div>
      <div className="hidden lg:block relative pt-24">
        <div className="relative w-[380px]" style={{ aspectRatio: "4/3" }}>
          <span className="corner-mark tl" /><span className="corner-mark tr" />
          <span className="corner-mark bl" /><span className="corner-mark br" />
          <img src="/img/home3.jpg" alt="Familia recibiendo las llaves de su nueva casa" className="w-full h-full object-cover p-4" data-testid="manifesto-photo" />
        </div>
      </div>
    </div>
  </section>
);

export const Letter = ({ t }) => (
  <section className="py-24 md:py-32 px-6" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="letter-section">
    <div className="max-w-[760px] mx-auto">
      <Reveal>
        <SectionLabel>{t.letter.label}</SectionLabel>
        <p className="text-2xl text-white/60 italic mb-6">{t.letter.salutation}</p>
        <p className="text-white/55 mb-8 leading-relaxed">{t.letter.intro}</p>
      </Reveal>
      <div className="space-y-4 mb-10">
        {t.letter.beliefs.map((b, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="belief-card lift-card p-5 flex gap-4 items-start" data-testid={`false-belief-${i + 1}`}>
              <span className="text-red-400 font-black shrink-0">✗</span>
              <p className="italic text-white/70 text-[15px] leading-relaxed">{b}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div className="h-px bg-[#4D9FFF] w-24 mb-10" />
        <p className="text-white/60 leading-[1.85] text-[15px] mb-8">{t.letter.revelation}</p>
        <p className="accent font-bold">{renderPh(t.letter.signoff)}</p>
      </Reveal>
    </div>
  </section>
);

export const Audience = ({ t }) => (
  <section id="learn" className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" data-testid="audience-section">
    <SectionBg img="/img/home3.jpg" />
    <div className="relative max-w-[1200px] mx-auto">
      <Reveal>
        <SectionLabel>{t.audience.label}</SectionLabel>
        <h2 className="h1-display text-3xl md:text-5xl mb-14">{t.audience.h2}</h2>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {t.audience.cards.map((c, i) => (
          <Reveal key={i} delay={i * 0.07}>
            <div className="audience-card p-6 md:p-8 h-full" data-testid={`audience-card-${i + 1}`}>
              <div className="text-3xl mb-4">{c.i}</div>
              <p className="font-bold text-sm md:text-base leading-snug uppercase tracking-wide">{c.t}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.4}>
          <div className="h-full flex items-center justify-center p-6">
            <CTA label={t.cta} testId="audience-cta" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Roadmap = ({ t }) => (
  <section className="py-20 border-y border-white/10" data-testid="roadmap-section">
    <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4">
      {t.roadmap.map((s, i) => (
        <Reveal key={i} delay={i * 0.1} className={`lift-card relative px-8 py-10 ${i > 0 ? "md:border-l border-white/10" : ""}`}>
          <span className="mono absolute top-2 right-4 text-[110px] font-black leading-none select-none" style={{ opacity: 0.04 }}>{s.n}</span>
          <div className="mono accent text-[10px] tracking-[0.3em] mb-3">{s.l}</div>
          <div className="font-black uppercase text-xl mb-3">{s.t}</div>
          <p className="text-white/45 text-sm leading-relaxed">{s.d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);
